import { ReactNode, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { TypoCallout, TypoHeading2, TypoTitle2 } from '@/components/Typography.tsx';
import { CollectionItem, CollectionItemProps } from '@/components/CollectionsList.tsx';
import ScoringGuideline from '@/components/ScoringGuideline.tsx';
import MissingTests from '@/components/MissingTests.tsx';
import Loader from '@/components/Loader.tsx';
import useUser from '@/hooks/useUser.ts';
import STUDY_CENTER_PAGES from '@/graphql/queries/study-center-pages.graphql';

import AccessWarning from './AccessWarning.tsx';
import { getPageWithCalculations, getTotalScore, StudyCenterPageProps } from './common.ts';

const StudyCenter = () => {
  const { user, loading } = useUser();

  if (loading) return <Loader />;
  if (!user.hasStudycenter) return <AccessWarning />;

  return (
    <>
      <div className="flex items-center justify-between px-10 pt-6 pb-4">
        <TypoHeading2 className="text-neutral-text-strong font-medium">
          Study Center
        </TypoHeading2>
        <ScoringGuideline title="About Study Center" />
      </div>
      <StudyCenterContent />
    </>
  );
};

const StudyCenterContent = () => {
  const { groups, loading } = useStudyCenterGroups();

  const scores = useMemo(() => {
    const allPages = groups.flatMap((group: any) => group.pages);
    const mathPages = allPages.filter((page: any) => page.group?.icon === 'MATH');
    const total = getTotalScore(allPages, 'currentPoints');
    const math = getTotalScore(mathPages, 'currentPoints');

    return { math, total };
  }, [groups]);

  if (loading) return <Loader />;

  if (!groups.length) {
    return (
      <div className="bg-neutral2 text-neutral2-text-soft rounded p-10 pb-8">
        <MissingTests text="No items yet." />
      </div>
    );
  }

  return (
    <StudyCenterGroups list={groups as StudyCenterPageProps['group'][]}>
      <div className="flex gap-5 justify-evenly items-stretch mb-10">
        <div className="flex-1 flex-shrink-0">
          <TypoTitle2 className="font-bold">{scores.total - scores.math}</TypoTitle2>
          <TypoCallout className="font-medium">READING AND WRITING FUNDAMENTALS SCORE</TypoCallout>
        </div>
        <div className="flex-1 flex-shrink-0">
          <TypoTitle2 className="font-bold">{scores.math}</TypoTitle2>
          <TypoCallout className="font-medium">MATH FUNDAMENTALS SCORE</TypoCallout>
        </div>
        <div className="flex-1 flex-shrink-0">
          <TypoTitle2 className="font-bold text-primary">{scores.total}</TypoTitle2>
          <TypoCallout className="font-medium">TOTAL FUNDAMENTALS SCORE</TypoCallout>
        </div>
      </div>
    </StudyCenterGroups>
  );
};

const StudyCenterGroups = ({
  list = [],
  children
}: { list?: StudyCenterPageProps['group'][]; children: ReactNode }) => {
  const formatted = list.map((group) => ({
    id: group?.studycenterGroupId ?? 0,
    name: group.name,
    type: 'collection',
    icon: group?.icon ?? 'PRACTICE',
    to: `/study-center/group/${group?.studycenterGroupId ?? 0}`,
  })) as CollectionItemProps[];

  return (
    <div className="bg-neutral2 text-neutral2-text-soft rounded p-10 pb-8">
      {children}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-6">
        {
          formatted.map((item) =>
            <CollectionItem key={item.id} className="w-full" {...item} />
          )
        }
      </div>
    </div>
  );
};

const useStudyCenterGroups = () => {
  const { data, loading } = useQuery(STUDY_CENTER_PAGES);

  const mapped = (data?.studyCenterPages ?? []).reduce((map: any, page: StudyCenterPageProps) => {
    const group = page.group || { name: 'Untitled' };

    if (!map.has(group.name)) map.set((group.name), { ...group, pages: [] });

    const mappedGroup = map.get(group.name);
    mappedGroup.pages.push(getPageWithCalculations(page));
    return map;
  }, new Map());

  const groups = Array.from(mapped.values());

  return {
    loading,
    groups,
  };
};

export default StudyCenter;
